import React from 'react';
import { MapAddress } from '../../../models/map-address.entity';
import http, { BASE_URL, BASE_URL_2, BASE_URL_3, OkPacket, ResponseErrorType } from '../../http';
import { getRBPStringId } from '../../../page/main/main-page-states';
import restaurantStaticApi from '../restaurant-static';

const settingApi = {

    /**
     * 测试服务器地址
     */
    async testServerUrl(): Promise<{ ok: number }> {
        return await http.get(`${BASE_URL_3}/ping/${getRBPStringId()}`, { timeout: 3000 });
    },
    // 测试数据库链接
    async testDataBseLink(): Promise<void> {
        return await http.get(`${BASE_URL_3}/kds/test/${getRBPStringId()}`, { timeout: 3000 });
    },
    // 获取选择的国家
    async getSelectedCountry(): Promise<{ id: number; name: string; value: string; }[]> {
        return await http.get(`${BASE_URL_3}/settings/48/${getRBPStringId()}`);
    },

    // 修改选择的国家
    async updateSelectedCountry(selectedCountry = 'ITA'): Promise<void> {
        return await http.patch(`${BASE_URL_3}/settings/48/${getRBPStringId()}`, {
            stringValue: selectedCountry
        });
    },

    // 保存google map 密钥
    async saveGoogleMapApiKey(key: string): Promise<string & ResponseErrorType> {
        return await http.patch(`${BASE_URL_3}/settings/map_key/${getRBPStringId()}`, {
            map_key: key
        });
    },

    // 获取谷歌密钥
    async getGoogleMapApiKey(): Promise<any> {
        return await http.get(`${BASE_URL_3}/settings/map_key/${getRBPStringId()}`);
    },

    // 获取当前餐厅地址
    async getRestaurantAddress(): Promise<{
        google_map_lng?: number;
        google_map_lat?: number;
        map_scope?: number;
        map_adress?: string;
        /** Google Place ID（若后端返回，用于评论二维码等） */
        place_id?: string;
    }> {
        return await http.get(`${BASE_URL_3}/settings/map_address/${getRBPStringId()}`);
    },

    // 保存google map 密钥
    async saveRestaurantAddress(param: {
        google_map_lng?: number;
        google_map_lat?: number;
        map_scope?: number;
        map_adress?: string;
        place_id?: string;
    }): Promise<any> {
        return await http.patch(`${BASE_URL_3}/settings/map_address/${getRBPStringId()}`, param);
    },

    // 根据id获取基础设置
    async getAuthorizationMessage(): Promise<any> {
        return await http.get(`${BASE_URL_3}/warrants/${getRBPStringId()}`);
    },

    // 根据id获取基础设置
    async getSettingById(id: number, rbpStringId?: string): Promise<any> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.get(`${BASE_URL_3}/settings/${id}/${sid}`);
    },

    // 根据id修改基础设置
    async patchSettingById(id: number, params: any, rbpStringId?: string): Promise<any> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/settings/${id}/${sid}`, {
            ...params
        });
    },

    /**
     * 按 id 修改设置项，body 可为数组等非 `{ stringValue }` 结构（与 RBP2 `patchSettingByIdByArr` 对齐，如 id 91/92）。
     */
    async patchSettingByIdByArr(id: number, params: any, rbpStringId?: string): Promise<any> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        return await http.patch(`${BASE_URL_3}/settings/${id}/${sid}`, { stringValue: params });
    },

    /**
     * 按 id 上传文件类设置（如 AEAT 证书），multipart。
     */
    async patchSettingByIdWithFile(id: number, file?: File, rbpStringId?: string): Promise<any> {
        const sid = (rbpStringId && rbpStringId.trim()) || getRBPStringId();
        const formData = new FormData();
        formData.append('file', file || '');
        return await http.patch(`${BASE_URL_3}/settings/${id}/${sid}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // 获取kwa轮次权重值
    async getDishWeigthForKWA(): Promise<any> {
        return await http.get(`${BASE_URL_2}/shark/round_weight_value/${getRBPStringId()}`);
    },

    // 修改kwa轮次权重值
    async postDishWeigthForKWA(param: any): Promise<any> {
        return await http.post(`${BASE_URL_2}/shark/round_weight_value/${getRBPStringId()}`, param);
    },

    // 获取所有基础设置
    async getSetting(): Promise<any> {
        return await http.get(`${BASE_URL_2}/settings/${getRBPStringId()}`);
    },

    /**
     * MOP 上传轮播图片（走 v1 公共静态资源，与 Apifox `POST /v1/public/{string_id}/restaurant-static/bulletin-mop-carousel-image/{index}` 一致）。
     * @param params.index - 槽位下标字符串 `0`–`4`
     * @param params.image - 待上传文件（`File`）
     */
    async postMopCarouselImg(params: { index: string; image: File }): Promise<any> {
        const { index, image } = params;
        const sid = getRBPStringId();
        const idx = Number.parseInt(String(index), 10);
        /** query `file_name`：优先取上传文件名，缺省时用槽位生成安全默认名 */
        const rawName = typeof image?.name === 'string' ? image.name.trim() : '';
        const queryFileName = rawName || `${index}.jpg`;
        return await restaurantStaticApi.post_restaurant_static_bulletin_mop_carousel_image(sid, idx, image, queryFileName);
    },

    /**
     * MOP 删除轮播图片（`DELETE /v1/public/{string_id}/restaurant-static/bulletin-mop-carousel-image/{index}?file_name=...`）。
     * @param index - 槽位下标 0–4
     * @param fileName - 当前图在设置/bundled 中的 **basename**（与上传返回 `file_name` 一致，须以 `{index}_` 开头）
     */
    async deleteMopCarouselImg(index: number, fileName: string): Promise<any> {
        return await restaurantStaticApi.delete_restaurant_static_bulletin_mop_carousel_image(
            getRBPStringId(),
            index,
            fileName,
        );
    },

    /**
     * 获取MOP专属菜单ids
     * @returns
     */
    async getMopSelectMenu(): Promise<any> {
        return await http.get(`${BASE_URL_2}/menus/mop/select/${getRBPStringId()}`);
    },

    /**
     * 修改MOP专属菜单
     * @param selectIds
     * @returns
     */
    async patchMopSelectMenu(selectIds: number[]): Promise<any> {
        return await http.patch(`${BASE_URL_2}/menus/mop/set/${getRBPStringId()}`, { selectedIds: selectIds });
    },

    /**
     * 获取MOP套餐设置
     * @returns
     */
    async getMopMenuItemGroup(): Promise<any> {
        return await http.get(`${BASE_URL_3}/menu_item/group/${getRBPStringId()}`);
    },

    /**
     * MOP套餐可选组必选满设置
     * @param param
     * @returns
     */
    async patchMopMenuItemFullSelect(param: { course_group_id: number, is_full_select: number }): Promise<any> {
        return await http.patch(`${BASE_URL_3}/menu_item_full_select/${getRBPStringId()}`, param);
    },

    /**
     * 校验 Smartway Cloud 密钥（与 dambuster `GET /v1/public/restaurant` + `secret-type` / `secret-value` 头一致，不再使用路径中的 `string_id`）。
     * @param key - 设置项 45 中的密钥密文，作为 `secret-value` 传递
     * @returns 成功时含 `restaurant`、`group` 等；失败时走统一 `error` 结构
     */
    async getSmartwayCloudKey(key: string): Promise<any> {
        return await http.get(`${BASE_URL}/public/restaurant`, {
            headers: {
                /** 公共 API 鉴权类型，与后端 `checkSecretKey` 校验一致 */
                'secret-type': 'PUBLIC_API',
                /** 与旧版 `/v2/public_key/:key/...` 路径中的 `key` 相同 */
                'secret-value': key,
            },
        });
    },

    /**
     * 删除Smartway Cloud密钥
     */
    async deletegybLicenseCode(): Promise<any> {
        return await http.delete(`${BASE_URL_3}/od/gyb_license_code/${getRBPStringId()}`);
    },

    /**
     * 校验 OD 密钥
     * @param param
     * @returns
     */
    async checkODKey(param: any): Promise<any> {
        return await http.get(`${BASE_URL_3}/check/od/secret_key/${getRBPStringId()}`, param);
    },

    /**
     * 删除 OD 密钥
     */
    async deleteODSecretKey(): Promise<any> {
        return await http.delete(`${BASE_URL_3}/od/secret-value/${getRBPStringId()}`);
    },

    /**
     * 获取 od 第三方项目
     */
    async getThirdPartyProjects(): Promise<any> {
        return await http.get(`${BASE_URL_3}/od/socket/info/${getRBPStringId()}`)
    },

    /**
     * 重新发送 od ws 连接
     */
    async odResend(): Promise<any> {
        return await http.post(`${BASE_URL_3}/od/socket/connection/${getRBPStringId()}`)
    },

    /**
     * 修改餐厅规则设置 - 一轮一次发送
     * @param param
     */
    async patchRestOneTimeOneSend(param: { on_off: string }): Promise<any> {
        return await http.patch(`${BASE_URL_2}/one_time_one_turn_send/on_off/${getRBPStringId()}`, param);
    },

    /**
     * 清空所有其他菜品标识
     */
    async deleteOtherDetailInfo(): Promise<any> {
        return await http.delete(`${BASE_URL_3}/dishes/clearOtherDetailInfo/${getRBPStringId()}`);
    },

    /**
     * 开启GPS点餐范围判定 1 开启 0 关闭
     * @returns
     */
    async get_google_map(): Promise<{ set_map: string }> {
        return await http.get(`${BASE_URL_3}/google_map/${getRBPStringId()}`)
    },
    /**
     * 开启GPS点餐范围判定 1 开启 0 关闭
     * @returns
     */
    async patch_google_map(setMap: string): Promise<string> {
        return await http.patch(`${BASE_URL_3}/google_map/${getRBPStringId()}`, { set_map: setMap })
    },

    /**
     * 餐厅地址相关设置
     * @returns
     */
    async get_settings_map_address(): Promise<MapAddress> {
        return await http.get(`${BASE_URL_3}/settings/map_address/${getRBPStringId()}`)
    },
    /**
     * 餐厅地址相关设置
     * @returns
     */
    async patch_settings_map_address(data: MapAddress): Promise<OkPacket> {
        return await http.patch(`${BASE_URL_3}/settings/map_address/${getRBPStringId()}`, data)
    },

    /**
     * 开启/关闭 桌台临时密码
     * @param onOff '0'关闭 '1'开启
     * @returns
     */
    async patch_order_head_password_on_off(onOff: string): Promise<string> {
        return await http.patch(`${BASE_URL_2}/order_head_password/on_off/${getRBPStringId()}`, {
            on_off: onOff
        })
    },
    /**
     * 修改扫码时间间隔
     * @param time_interval '0'关闭 '1'开启
     * @returns
     */
    async patch_qop_scan_time_interval(time_interval: number): Promise<any> {
        return await http.patch(`${BASE_URL_2}/shark/time_interval_value_update/${getRBPStringId()}`, {
            time_interval
        })
    },
    // 清空桌台历史临时密码
    async post_order_head_password_del(): Promise<string> {
        return await http.post(`${BASE_URL_2}/order_head_password/del/${getRBPStringId()}`)
    },

    /**
     * 生成QOP二维码
     * @param image
     * @param qrCodeData
     * @param type
     * @param theme
     * @returns
     */
    async post_qr_code_draw_image(image: Blob, qrCodeData: QrCodeData, type: 'GEN' | 'LOOK'): Promise<{ image: string[] }> {
        const formData = new FormData();
        formData.append('image', image);
        formData.append('qrcode_data', JSON.stringify(qrCodeData));
        formData.append('type', type);
        return await http.post(`${BASE_URL_3}/qrcode/batch-draw-image/${getRBPStringId()}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    },


    /**
     * 生成桌台二维码
     * @param qrCodeData
     */
    async post_table_qr_code_draw_image(qrCodeData: QrCodeData): Promise<{ image: string[] }> {
        const formData = new FormData();
        formData.append('qrcode_data', JSON.stringify(qrCodeData));
        return await http.post(`${BASE_URL_3}/table/qrcode/${getRBPStringId()}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    },

    /**
     * 打印二维码
     * @param qrCodeData
     */
    async post_table_qr_code_print(qrCodeData: any): Promise<string[]> {
        const formData = new FormData();
        formData.append('qrcode_data', JSON.stringify(qrCodeData));
        return await http.post(`${BASE_URL_3}/qrcode/print/${getRBPStringId()}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
    },

    /**
     * 获得全球时区信息
     */
    async getGlobalTimezones(): Promise<Timezone[]> {
        return await http.get(`${BASE_URL_3}/settings/worldwide_zone/${getRBPStringId()}`);
    },

    /**
     * 获取当前设置的时区 id number query 设置的id
     * name string query 设置时区名字参数
     * value number string query
     */
    async getCurrentTimezone(): Promise<Array<CurrentTimezoneBack>> {
        return await http.get(`${BASE_URL_3}/settings/zone/${getRBPStringId()}`);
    },

    /**
     * 修改时区    id number body 时区的id
     */
    async verifyCurrentTimezone(id: number): Promise<VerifyTimezoneBack & ResponseErrorType> {
        return await http.patch(`${BASE_URL_3}/settings/zone/${getRBPStringId()}`, { id });
    },

    /**
     * 修改菜品价格兼容性
     */
    async patch_menu_item_main(set_item_main: string): Promise<any> {
        return await http.patch(`${BASE_URL_3}/set_item_main/${getRBPStringId()}`, { set_item_main });
    },

    /**
     * 获取菜品价格兼容性
     */
    async get_menu_item_main(): Promise<any> {
        return await http.get(`${BASE_URL_3}/set_item_main/${getRBPStringId()}`);
    },

    /**
     * 上传 QOP 自定义模板图（`POST /v1/public/{string_id}/restaurant-static/qop-mould-image/{id}`）。
     * @param tempId - 模板 id（与历史 v3 路径一致）
     * @param imageFile - 图片文件
     */
    async post_qop_temp_image(tempId: string, imageFile: File): Promise<any> {
        return await restaurantStaticApi.post_restaurant_static_qop_mould_image(getRBPStringId(), String(tempId), imageFile);
    },

    /**
     * 删除 QOP 自定义模板图（`DELETE .../qop-mould-image/{id}`）。
     * @param id - 模板 id
     */
    async delete_qop_temp_image(id: number): Promise<any> {
        return await restaurantStaticApi.delete_restaurant_static_qop_mould_image(getRBPStringId(), String(id));
    },

    // 断开od的ws
    async disconnectOdWs(param: { secretValue: string }): Promise<any> {
        return await http.post(`${BASE_URL_3}/od/socket/disconnect/${getRBPStringId()}`, param);
    },

    /**
     * 检查隧道配置（`GET /v3/check-permission/{当前餐厅 string_id}`），仅隧道连通性；不包含门店程序版本门禁。
     */
    async checkPermission(config?: any): Promise<any> {
        return await http.get(`${BASE_URL_3}/check-permission/${getRBPStringId()}`, config);
    },

    /**
     * 按指定餐厅 `string_id` 校验隧道与实时库是否可用（`GET /v3/check-permission/{string_id}`）。
     * 与 `checkPermission` 区别：后者固定使用当前聚焦餐厅的 `getRBPStringId()`；本方法用于跨门店操作前校验目标店。
     * 版本门禁请另调 **`GET /v1/restaurant/{string_id}/version-check`**（`apiManage.get_restaurant_version_check`），勿混在本接口响应中解读。
     * @param restaurantStringId - 目标餐厅 `string_id`（路径参数，与 Apifox 示例 `9RRJ8` 同形态）
     * @param config - Axios 可选配置（如 `cancelToken`）
     */
    async checkPermissionByRestaurantStringId(restaurantStringId: string, config?: any): Promise<any> {
        const sid = (restaurantStringId && restaurantStringId.trim()) || '';
        return await http.get(`${BASE_URL_3}/check-permission/${encodeURIComponent(sid)}`, config);
    },
    /**
     * RBP 货币
     */
    async get_rbp_settings_currency(): Promise<any> {
        return await http.get(`${BASE_URL_3}/settings/currency/${getRBPStringId()}`);
    },
    /**
     * 按指定餐厅码拉取云端货币（菜单库模块内选店等与顶栏当前店分离的场景）
     * @param restaurantStringId - RBP `string_id`
     */
    async get_rbp_settings_currency_by_string_id(restaurantStringId: string): Promise<any> {
        const sid = (restaurantStringId && restaurantStringId.trim()) || '';
        return await http.get(`${BASE_URL_3}/settings/currency/${encodeURIComponent(sid)}`);
    },

    /**
     * 同步游乐园/娱乐项目侧预定时间（与 RBP2 `async_park_time` 一致；Shell2 走 v3 且带当前餐厅 `string_id`）
     */
    async synchronization_amusement_time(): Promise<any> {
        return await http.get(`${BASE_URL_3}/synchronization_amusement_time/${getRBPStringId()}`);
    },
    /**
     * 更新 shark 规则（如单品限制 id=3），与 RBP2 `POST /api/v3/update/shark_settings` 语义一致，云上路径带餐厅 `string_id`。
     * @param params - 与 RBP2 `post_update_shark_settings` 相同字段
     */
    async post_update_shark_settings(params: {
        id: number;
        set: { init: boolean; enable: boolean } | Record<string, unknown>;
        rule_lnr_enable?: number;
    }): Promise<any> {
        return await http.post(`${BASE_URL_3}/update/shark_settings/${getRBPStringId()}`, params);
    },

}

export default settingApi;

// 生成QOP二维码
export type QrCodeData = {
    // wifi
    wifi?: Wifi[];
    // web
    web?: Web[];
    // 样式
    textSettings?: TextSettings
}

// wifi
export type Wifi = {
    type: 'wifi';
    name: string;
    password: string;
    /** 与 RBP2 `request/api/setting` 中 `Wifi` 类型一致 */
    security: 'None' | 'WPA' | 'WEP';
    top: number;
    left: number;
}
// web
export type Web = {
    type: 'web',
    url: string,
    tableId: number | string,
    tableName: string,
    top: number,
    left: number
}

type TextSettings = {
    // 桌台名称文字样式
    tableName: React.CSSProperties;
    // 餐厅名称文字样式
    restaurantName: React.CSSProperties;
    // 餐厅Logo文字样式
    restaurantLogo: React.CSSProperties;
    // wifi信息文本样式
    wifiInfo: React.CSSProperties;
    // wifi icon样式
    wifiIcon: React.CSSProperties;
    // table icon样式
    tableInfoIcon: React.CSSProperties;
    // cus code box样式
    cusCodeBox: React.CSSProperties;
}

type Timezone = {
    id: number,
    time_zone: string,
    zone_name: string
}
type CurrentTimezoneBack = {
    'id': number,
    'name': 'zone_id',
    'value': string
}

type VerifyTimezoneBack = {
    'fieldCount': 0,
    'affectedRows': 1,
    'insertId': 0,
    'serverStatus': 3,
    'warningCount': 0,
    'message': '(Rows matched: 1  Changed: 1  Warnings: 0',
    'protocol41': true,
    'changedRows': 1
}
